/**
 * CreditCalculationService — Calcul de l'état des conversations IA
 *
 * 📜 Doctrine pricing (11 mai 2026) — packages/core/src/constants/quotas.ts
 *    ANONYMOUS : 3 conversations IA à vie (aimant d'acquisition)
 *    FREE      : 5 conversations IA à vie (non renouvelables)
 *    SOLO      : illimité (Fair Use invisible)
 *
 * Pas de reset mensuel : les quotas ANONYMOUS et FREE sont à vie.
 * Les champs `resetDate` / `daysUntilReset` sont conservés dans le type
 * pour la compatibilité ascendante mais valent toujours `null`.
 */

import { CONVERSATION_QUOTAS, type PlanId } from "@core/constants/quotas";
import i18n from "@core/i18n";

export type CreditPlanType = PlanId; // 'ANONYMOUS' | 'FREE' | 'SOLO'

export interface CreditInfo {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  resetDate: Date | null;
  daysUntilReset: number | null;
  isNearLimit: boolean;
  isAtLimit: boolean;
  planType: CreditPlanType;
}

export interface CreditCalculationOptions {
  includeHistory?: boolean;
  historyLimit?: number;
}

class CreditCalculationService {
  /**
   * Calcule les informations complètes sur les conversations IA consommées.
   */
  calculateCredits(
    usedCredits: number,
    planType: CreditPlanType,
    _options: CreditCalculationOptions = {},
  ): CreditInfo {
    const limit = this.getCreditLimit(planType);
    const isUnlimited = !Number.isFinite(limit);
    const remaining = isUnlimited
      ? Number.POSITIVE_INFINITY
      : Math.max(0, limit - usedCredits);
    const percentage = isUnlimited
      ? 0
      : limit > 0
        ? Math.min((usedCredits / limit) * 100, 100)
        : 100;

    return {
      used: usedCredits,
      limit,
      remaining,
      percentage,
      // Plans à vie → pas de reset programmé.
      resetDate: null,
      daysUntilReset: null,
      isNearLimit: !isUnlimited && percentage >= 80 && percentage < 100,
      isAtLimit: !isUnlimited && percentage >= 100,
      planType,
    };
  }

  /**
   * Plafond de conversations IA selon le plan. SOLO est traité comme
   * illimité (retourne Infinity) — à utiliser via isFinite().
   */
  private getCreditLimit(planType: CreditPlanType): number {
    switch (planType) {
      case "ANONYMOUS":
        return CONVERSATION_QUOTAS.ANONYMOUS; // 3
      case "FREE":
        return CONVERSATION_QUOTAS.FREE; // 5
      case "SOLO":
        return Number.POSITIVE_INFINITY;
      default:
        return CONVERSATION_QUOTAS.FREE;
    }
  }

  /**
   * Conservé pour compatibilité d'API. Les plans FREE et SOLO sont
   * à vie → renvoie toujours la chaîne vide.
   */
  formatDaysUntilReset(daysUntilReset: number | null): string {
    if (daysUntilReset == null || daysUntilReset < 0) return "";
    // Compatibilité : certains appelants passent encore un nombre.
    if (daysUntilReset === 0) return i18n.t("credits.reset.today");
    if (daysUntilReset === 1) return i18n.t("credits.reset.tomorrow");
    return i18n.t("credits.reset.inDays", { count: daysUntilReset });
  }

  /**
   * Message de notification aligné avec la doctrine :
   *   - ANONYMOUS : invitation à créer un compte gratuit
   *   - FREE      : état des conversations IA à vie
   *   - SOLO      : aucun message (conversations illimitées)
   */
  generateNotificationMessage(creditInfo: CreditInfo): string {
    const { used, limit, remaining, isNearLimit, isAtLimit, planType } =
      creditInfo;

    if (planType === "SOLO") {
      return i18n.t("credits.notification.solo");
    }

    if (planType === "ANONYMOUS") {
      return i18n.t("credits.notification.anonymous");
    }

    // FREE
    if (isAtLimit) {
      return i18n.t("credits.notification.atLimit", { used, limit });
    }
    if (isNearLimit) {
      return i18n.t("credits.notification.nearLimit", { used, limit });
    }
    if (remaining <= 3) {
      return i18n.t("credits.notification.remaining", { remaining });
    }
    return i18n.t("credits.notification.available", { remaining });
  }

  /**
   * CTA standard : SOLO n'en a jamais, les autres pointent vers /pricing
   * ou /auth selon l'état.
   */
  generateCallToAction(
    creditInfo: CreditInfo,
  ): { label: string; url: string } | null {
    const { planType, isAtLimit, isNearLimit } = creditInfo;

    if (planType === "SOLO") return null;

    if (planType === "ANONYMOUS") {
      return { label: i18n.t("credits.cta.createAccount"), url: "/auth" };
    }

    // FREE
    if (isAtLimit) {
      return { label: i18n.t("credits.cta.upgradeSolo"), url: "/pricing" };
    }
    if (isNearLimit) {
      return { label: i18n.t("credits.cta.upgradeSolo"), url: "/pricing" };
    }
    return { label: i18n.t("credits.cta.viewUsage"), url: "/journal" };
  }

  /**
   * Message d'encouragement affiché en bas du panneau de notifications.
   */
  generateEncouragementMessage(creditInfo: CreditInfo): string {
    const { planType, isAtLimit, isNearLimit } = creditInfo;

    if (planType === "SOLO") {
      return i18n.t("credits.encouragement.solo");
    }

    if (planType === "ANONYMOUS") {
      return i18n.t("credits.encouragement.anonymous");
    }

    if (isAtLimit) {
      return i18n.t("credits.encouragement.atLimit");
    }
    if (isNearLimit) {
      return i18n.t("credits.encouragement.nearLimit");
    }
    return i18n.t("credits.encouragement.default");
  }

  /**
   * Priorité de la notification (pilote couleur du badge).
   */
  getNotificationPriority(
    creditInfo: CreditInfo,
  ): "critique" | "élevée" | "moyenne" | "faible" {
    const { planType, isAtLimit, isNearLimit, remaining } = creditInfo;
    if (planType === "SOLO") return "faible";
    if (isAtLimit) return "critique";
    if (isNearLimit) return "élevée";
    if (Number.isFinite(remaining) && remaining <= 3) return "moyenne";
    return "faible";
  }

  /**
   * L'historique réel est à charger côté serveur (table quota_tracking).
   * La simulation précédente a été supprimée — elle générait du bruit.
   */
  calculateHistory(): [] {
    return [];
  }

  /**
   * La cloche s'affiche dès qu'on est en situation critique / proche limite ;
   * sinon on respecte un cooldown de 24h pour éviter le spam.
   */
  shouldShowNotification(
    creditInfo: CreditInfo,
    lastShown: Date | null,
  ): boolean {
    const { planType, isAtLimit, isNearLimit, remaining } = creditInfo;

    if (planType === "SOLO") return false;
    if (planType === "ANONYMOUS") return true; // incite à l'inscription
    if (isAtLimit || isNearLimit) return true;
    if (Number.isFinite(remaining) && remaining <= 3) return true;

    if (lastShown) {
      const hoursSince = (Date.now() - lastShown.getTime()) / (1000 * 60 * 60);
      if (hoursSince < 24) return false;
    }
    return false;
  }
}

export const creditCalculationService = new CreditCalculationService();
export default CreditCalculationService;
